<template>
  <v-dialog :model-value="modelValue" max-width="620" persistent @update:model-value="close">
    <v-card>
      <v-card-title>{{ $t("admin.contracts.manualDelete.dialogTitle") }}</v-card-title>
      <v-card-text>
        <v-alert type="error" density="compact" class="mb-4">
          {{ $t("admin.contracts.manualDelete.warning", { company: company.company_name }) }}
        </v-alert>

        <!-- The dry-run runs the real delete plan and rolls it back, so this list
             is what actually disappears, not an estimate. -->
        <div class="company-delete__preview">
          <v-progress-linear v-if="loadingPreview" indeterminate color="error" class="mb-2" />
          <template v-else-if="preview">
            <p class="company-delete__total">
              {{ $t("admin.contracts.manualDelete.previewTotal", { total: preview.total }) }}
            </p>
            <ul v-if="previewRows.length" class="company-delete__rows">
              <li v-for="row in previewRows" :key="row.table">
                <span>{{ row.table }}</span><strong>{{ row.count }}</strong>
              </li>
            </ul>
            <p v-else class="company-delete__empty">{{ $t("admin.contracts.manualDelete.previewEmpty") }}</p>
          </template>
        </div>

        <v-alert v-if="errorMessage" type="error" density="compact" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <p class="mb-2">{{ $t("admin.contracts.manualDelete.confirmationHint") }}</p>
        <v-text-field
          v-model="confirmationName"
          :label="$t('admin.contracts.manualDelete.confirmationLabel')"
          placeholder="DELETE"
          autocomplete="off"
          hide-details="auto"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="deleting" @click="close">{{ $t("app.actions.cancel") }}</v-btn>
        <v-btn color="error" variant="flat" :loading="deleting" :disabled="!confirmed || !preview || loadingPreview" @click="submit">
          {{ $t("admin.contracts.manualDelete.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { deleteCompanyPermanently, previewCompanyDeletion } from "./adminContractsApi.mjs";

export default {
  name: "AdminCompanyDeleteDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    company: { type: Object, required: true },
  },
  emits: ["update:modelValue", "deleted"],
  data() {
    return {
      confirmationName: "",
      errorMessage: "",
      loadingPreview: false,
      deleting: false,
      preview: null,
    };
  },
  computed: {
    previewRows() {
      const counts = this.preview?.counts || {};
      return Object.entries(counts)
        .map(([table, count]) => ({ table, count }))
        .sort((a, b) => b.count - a.count);
    },
    confirmed() {
      return this.confirmationName === "DELETE";
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.loadPreview();
    },
  },
  methods: {
    async loadPreview() {
      this.confirmationName = "";
      this.errorMessage = "";
      this.preview = null;
      this.loadingPreview = true;
      try {
        const { data } = await previewCompanyDeletion(this.company.company_id);
        this.preview = data;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || this.$t("admin.contracts.manualDelete.errors.preview");
      } finally {
        this.loadingPreview = false;
      }
    },
    close() {
      if (this.deleting) return;
      this.$emit("update:modelValue", false);
    },
    async submit() {
      if (!this.confirmed || !this.preview || this.loadingPreview) return;
      this.deleting = true;
      this.errorMessage = "";
      try {
        const { data } = await deleteCompanyPermanently(
          this.company.company_id,
          this.confirmationName
        );
        this.$emit("deleted", data);
        this.$emit("update:modelValue", false);
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || this.$t("admin.contracts.manualDelete.errors.failed");
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.company-delete__preview { margin-bottom: 16px; }
.company-delete__total { margin: 0 0 6px; color: var(--app-text-strong); font-size: 0.86rem; font-weight: 600; }
.company-delete__rows { margin: 0; padding: 0; list-style: none; max-height: 190px; overflow-y: auto; }
.company-delete__rows li { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06); font-size: 0.78rem; }
.company-delete__rows span { color: var(--app-text-default); opacity: 0.75; }
.company-delete__rows strong { color: var(--app-text-strong); }
.company-delete__empty { margin: 0; color: var(--app-text-default); font-size: 0.78rem; opacity: 0.7; }
</style>
